/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {ReactNode, useContext} from "react";
import {DB_Patient} from "@app/app-shared/patient";
import {getUserOrgRoles, hasPermissionOrgRoles} from "@modules/utils";
import {NoPermissionsPanel} from "../../home/NoPermissionsPanel";
import {ID_ALL, OrgRoleLevel} from "@app/app-shared/permission";

export type PatientsMap = { [_id: string]: DB_Patient | undefined };
export type PatientsContextType = {
    patients: PatientsMap
    patientIds: string[]
    create: (patient: DB_Patient) => Promise<void>
    delete: (patientId: string) => Promise<void>
    setDemo: (demo: boolean) => void
    demo: boolean
}

export const PatientsContext = React.createContext<PatientsContextType>({
    patientIds: [],
    patients: {},
    create: async () => {
    },
    delete: async () => {
    },
    setDemo: (demo: boolean) => {

    },
    demo: false
});

type Props = {
    children:  (ctx: PatientsContextType) => ReactNode;
    roles: OrgRoleLevel[]
};
export const PatientContextProvider: React.FC<Props> = ({children, roles}) => {
    const ctx = useContext(PatientsContext)
    const {patientIds, patients} = ctx;
    const userOrgRoles = getUserOrgRoles();
    if (!userOrgRoles)
        return <NoPermissionsPanel/>;
    const allowedOrgs = new Set<string>()

    Object.keys(userOrgRoles).filter((orgId) => {
        if (hasPermissionOrgRoles(userOrgRoles, orgId, roles))
            allowedOrgs.add(orgId)
    })

    const hasAll = allowedOrgs.has(ID_ALL);
    const pMap: PatientsMap = {};
    const pIds: string[] = [];
    for (const patientId of patientIds) {
        const patient = patients[patientId];
        if (patient && (hasAll || allowedOrgs.has(patient.organizationId))) {
            pMap[patient._id] = patient;
            pIds.push(patient._id);
        }
    }
    const newContext = {...ctx, patients: pMap, patientIds: pIds};
    return (
        <PatientsContext.Provider value={newContext}>
            {children(newContext)}
        </PatientsContext.Provider>
    )
}